import { Suspense } from 'react'
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { AuthLayout as AuthLayoutComponent } from '@/layouts'
import { RouteLoadingFallback } from '@/components/loading/RouteLoadingFallback'

/**
 * Auth Layout Route (Pathless)
 *
 * Routes under this layout:
 * - / (Home)
 * - /dashboard
 * - /employees
 * - /workforce
 * - /vacation
 * - /calendar
 * - /training
 * - /performance
 * - All other authenticated routes
 *
 * The underscore prefix makes this route pathless - it provides layout
 * without contributing to the URL structure.
 *
 * Layout includes:
 * - Header (top bar) with user menu and navigation controls
 * - Sidebar (desktop) with application navigation
 * - Mobile sidebar drawer
 * - Main content area with responsive padding
 */
export const Route = createFileRoute('/_auth')({
  beforeLoad: async ({ location }) => {
    // Check if user is authenticated by looking at localStorage
    // This is a simple check - the AuthProvider will handle the full auth flow
    const storedUserProfile = localStorage.getItem('user-profile')

    // Check if this is an OAuth callback (has 'code' and 'state' params)
    const searchParams = new URLSearchParams(location.search)
    const isOAuthCallback = searchParams.has('code') && searchParams.has('state')

    console.error('[ROUTE-GUARD] _auth beforeLoad check:', {
      pathname: location.pathname,
      hasUserProfile: !!storedUserProfile,
      isOAuthCallback
    })

    // Don't redirect if this is an OAuth callback - let the auth handler process it
    if (!storedUserProfile && !isOAuthCallback) {
      console.error('[ROUTE-GUARD] ❌ No user profile, redirecting to login')
      throw redirect({
        to: '/login',
      })
    }

    if (isOAuthCallback) {
      console.error('[ROUTE-GUARD] 🔐 OAuth callback detected, allowing auth handler to process')
    } else {
      console.error('[ROUTE-GUARD] ✅ User authenticated, proceeding')
    }
  },
  component: AuthLayout,
})

function AuthLayout() {
  return (
    <AuthLayoutComponent>
      <Suspense fallback={<RouteLoadingFallback />}>
        <Outlet />
      </Suspense>
    </AuthLayoutComponent>
  )
}
